import { AxiosError } from 'axios';
import {
    containsBlockedContent,
    getInappropriateMessage,
    INAPPROPRIATE_FIELD_KEY,
    INAPPROPRIATE_FIELD_MESSAGE,
} from './contentModeration';

export const MIN_PRICE = 0.5;
export const MAX_PRICE = 5000;
export const MIN_TITLE_LENGTH = 3;

/**
 * Parse a money input to exact euro cents (2 decimals).
 * Accepts comma or dot as decimal separator. Does not invent whole-euro rounding.
 */
export function parseMoneyInput(value: string): number {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) return 0;
    const n = Number(normalized);
    if (!Number.isFinite(n)) return 0;
    // Match SQL decimal(18,2) / AwayFromZero cent rounding
    return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Display amount with exactly two decimals (exact cents, no whole-euro rounding). */
export function formatMoney(amount: number | null | undefined): string {
    const n = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
    return (Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2);
}
export const MAX_TITLE_LENGTH = 200;
export const MAX_DESCRIPTION_LENGTH = 1000;
export const MAX_IMAGES = 5;
export const MIN_IMAGES_ON_CREATE = 1;
export const MAX_CATEGORIES = 2;
export const MIN_CATEGORIES = 1;
export const MAX_TAGS = 5;
export const MAX_TAG_LENGTH = 30;
export const MAX_IMAGE_BYTES = 12 * 1024 * 1024;

export const TITLE_REGEX = /^[\p{L}\p{N}\s\-.,'()&/]+$/u;

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png'] as const;
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'] as const;

export interface ProductFormValues {
    title: string;
    description: string;
    price: number;
    categories: string[];
    tags: string[];
    imageCount: number;
    isCreate: boolean;
    /** Nested free-text / enum specification values: category → name → value */
    specifications?: Record<string, Record<string, string>>;
}

export type ProductFieldErrors = Record<string, string>;

export function validateProductForm(values: ProductFormValues): ProductFieldErrors {
    const errors: ProductFieldErrors = {};
    const inappropriate = getInappropriateMessage(true);

    const trimmedTitle = values.title.trim();
    if (trimmedTitle.length < MIN_TITLE_LENGTH) {
        errors.title = 'form.productform.errors.title.min';
    } else if (trimmedTitle.length > MAX_TITLE_LENGTH) {
        errors.title = 'form.productform.errors.title.max';
    } else if (!TITLE_REGEX.test(trimmedTitle)) {
        errors.title = 'form.productform.errors.title.invalid';
    } else if (containsBlockedContent(trimmedTitle)) {
        errors.title = inappropriate;
    }

    const trimmedDescription = values.description.trim();
    if (!trimmedDescription) {
        errors.description = 'form.productform.errors.description.required';
    } else if (trimmedDescription.length > MAX_DESCRIPTION_LENGTH) {
        errors.description = 'form.productform.errors.description.max';
    } else if (containsBlockedContent(trimmedDescription)) {
        errors.description = inappropriate;
    }

    if (values.price < MIN_PRICE || values.price > MAX_PRICE) {
        errors.price = 'form.productform.errors.price.range';
    }

    const categories = values.categories.filter((c) => c.trim().length > 0);
    if (categories.length < MIN_CATEGORIES) {
        errors.categories = 'form.productform.errors.categories.min';
    } else if (categories.length > MAX_CATEGORIES) {
        errors.categories = 'form.productform.errors.categories.max';
    }

    const normalizedTags = values.tags
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

    if (normalizedTags.length > MAX_TAGS) {
        errors.tags = 'form.productform.errors.tags.max';
    } else if (normalizedTags.some((tag) => tag.length > MAX_TAG_LENGTH)) {
        errors.tags = 'form.productform.errors.tags.length';
    } else if (normalizedTags.some((tag) => containsBlockedContent(tag))) {
        errors.tags = inappropriate;
    }

    if (values.isCreate && values.imageCount < MIN_IMAGES_ON_CREATE) {
        errors.imageUrls = 'form.productform.errors.images.required';
    }
    if (values.imageCount > MAX_IMAGES) {
        errors.imageUrls = 'form.productform.errors.images.max';
    }

    // Specification free-text / any value (field key matches ProductForm: "{cat}-{specName}")
    if (values.specifications) {
        for (const [cat, specs] of Object.entries(values.specifications)) {
            if (!specs) continue;
            for (const [specName, value] of Object.entries(specs)) {
                if (value && containsBlockedContent(value)) {
                    errors[`${cat}-${specName}`] = inappropriate;
                }
            }
        }
    }

    return errors;
}

export function validateImageFiles(files: File[]): ProductFieldErrors {
    const errors: ProductFieldErrors = {};

    for (const file of files) {
        if (file.size > MAX_IMAGE_BYTES) {
            errors.imageUrls = 'form.productform.errors.images.size';
            break;
        }

        const extension = file.name.includes('.')
            ? `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`
            : '';

        const typeAllowed = ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number]);
        const extensionAllowed = ALLOWED_IMAGE_EXTENSIONS.includes(
            extension as (typeof ALLOWED_IMAGE_EXTENSIONS)[number]
        );

        if (!typeAllowed && !extensionAllowed) {
            errors.imageUrls = 'form.productform.errors.images.type';
            break;
        }
    }

    return errors;
}

const BACKEND_FIELD_ALIASES: Record<string, string> = {
    imageUrls: 'imageUrls',
    images: 'imageUrls',
};

export function parseAxiosValidationErrors(err: unknown): ProductFieldErrors {
    if (!(err instanceof AxiosError) || !err.response?.data) {
        return {};
    }

    const data = err.response.data;
    let rawErrors: any = null;

    // Handle { errors: {...} } or { Errors: {...} }
    if (data.errors) rawErrors = data.errors;
    else if (data.Errors) rawErrors = data.Errors;

    // Handle ASP.NET ModelState shape: { field: { errors: [{errorMessage}] }, ... } or array
    if (!rawErrors && typeof data === 'object') {
        rawErrors = {};
        for (const [field, val] of Object.entries(data as any)) {
            if (val && typeof val === 'object' && 'errors' in val) {
                const msgs = (val as any).errors;
                rawErrors[field] = Array.isArray(msgs) ? msgs.map((m: any) => m.errorMessage || m).join(', ') : msgs;
            } else if (Array.isArray(val)) {
                rawErrors[field] = val.join(', ');
            }
        }
    }

    if (!rawErrors || typeof rawErrors !== 'object') {
        // fallback to top level message
        if (data.message) return { General: data.message };
        return {};
    }

    const parsed: ProductFieldErrors = {};
    for (const [field, message] of Object.entries(rawErrors)) {
        const normalizedField = BACKEND_FIELD_ALIASES[field] ?? field;
        let msg = message;
        if (Array.isArray(msg)) msg = msg.map((m: any) => (typeof m === 'string' ? m : m.errorMessage || m)).join(', ');
        else if (msg && typeof msg === 'object' && 'errorMessage' in msg) msg = (msg as any).errorMessage;
        let text = typeof msg === 'string' ? msg : String(msg);
        // Map backend ContentModeration English copy → form translation key (same FieldError style)
        if (text === INAPPROPRIATE_FIELD_MESSAGE || text.includes('language that is not allowed')) {
            text = INAPPROPRIATE_FIELD_KEY;
        }
        parsed[normalizedField] = text;
    }

    return parsed;
}

export function mergeFieldErrors(
    clientErrors: ProductFieldErrors,
    serverErrors: ProductFieldErrors
): ProductFieldErrors {
    return { ...clientErrors, ...serverErrors };
}

export function hasFieldErrors(errors: ProductFieldErrors): boolean {
    return Object.keys(errors).length > 0;
}